import { formatDistanceToNow, formatDistanceToNowStrict } from "date-fns";
import { ja } from "date-fns/locale";
import { format, utcToZonedTime } from "date-fns-tz";
import { ASAP } from "downsample";
import * as Highcharts from "highcharts";
import Accessibility from "highcharts/modules/accessibility";
import Boost from "highcharts/modules/boost";
import ExportData from "highcharts/modules/export-data";
import Exporting from "highcharts/modules/exporting";
import { nth } from "lodash-es";

function createPText(string: string) {
  const newPElement = document.createElement("p");
  newPElement.innerText = string;
  return newPElement;
}

function rate(
  dataTimeArray: [number, number][],
  dataScale?: number,
  appendExtraEnd?: boolean,
) {
  let scale = 1;
  if (dataScale) {
    scale = dataScale;
  } else {
    for (let i = 1; i < dataTimeArray.length; i++) {
      scale = Math.sign(dataTimeArray[i][1] - dataTimeArray[i - 1][1]);
      if (scale !== 0) {
        break;
      }
    }
  }
  const startArray = dataTimeArray.slice(0, -1);
  const endArray = dataTimeArray.slice(1);
  const outArray: [number, number][] = [];
  for (let i = 0; i < startArray.length; i++) {
    const [startTime, startData] = startArray[i];
    const [endTime, endData] = endArray[i];
    const rate = ((endData - startData) * scale) / (endTime - startTime);
    outArray.push([startTime, rate]);
  }
  if (appendExtraEnd)
    outArray.push([
      dataTimeArray[dataTimeArray.length - 1][0],
      outArray[outArray.length - 1][1],
    ]);
  return outArray;
}

function calcETA(hpTime: [number, number][], target: number, n: number) {
  const removedZero = hpTime.filter(([_, hp]) => hp !== 0);
  n = Math.min(n, removedZero.length);
  const [lastTime, lastData] = nth(removedZero, -1) || [1, 1];
  const [firstTime, firstData] = nth(removedZero, -n) || [0, 0];
  const dataDiff = lastData - firstData;
  const timeDiff = lastTime - firstTime;
  let avgRate = dataDiff / timeDiff;
  if (avgRate === 0) {
    avgRate = Math.sign(dataDiff);
  }
  const timeRemaining = (target - lastData) / avgRate;
  return lastTime + timeRemaining;
}

function formatTimeStamp(timestamp: Date, langJa?: boolean, timeZone?: string) {
  if (langJa) {
    return format(timestamp, "MMMdo HH:mm zzz", {
      timeZone,
      locale: ja,
    });
  } else {
    return format(timestamp, "M/d HH:mm zzz", {
      timeZone,
    });
  }
}

function myFormatDistanceToNow(futureDate: Date): string {
  const now = new Date();
  const diffMs = futureDate.getTime() - now.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const dayStr = days > 1 ? `${days} days ` : days === 1 ? "1 day " : "";
  const hrStr = hours > 1 ? `${hours} hours ` : hours === 1 ? "1 hour " : "";
  const minStr =
    minutes > 1 ? `${minutes} minutes` : minutes === 1 ? "1 minute" : "";
  return `${dayStr}${hrStr}${minStr}`;
}

function futureStrings(
  timestamp: number,
  strict: boolean,
  langJa: boolean,
): [string, string] {
  const futureDate = new Date(timestamp * 1000);
  const futureString = formatTimeStamp(futureDate, langJa);
  const locale = langJa ? ja : undefined;
  const addSuffix = true;
  if (strict) {
    const difference = langJa
      ? formatDistanceToNowStrict(futureDate, {
          roundingMethod: "floor",
          addSuffix,
          locale,
        })
      : myFormatDistanceToNow(futureDate);
    return [futureString, difference];
  } else {
    const difference = formatDistanceToNow(futureDate, {
      addSuffix,
      locale,
    });
    return [futureString, difference];
  }
}

const s = (n: number, unit: string) => `${n} ${unit}${n > 1 ? "s" : ""}`;

function getDuration(start: number, end: number, langJa: boolean = false) {
  let length_second = end - start;
  const days = Math.floor(length_second / 86400);
  length_second %= 86400;
  const hours = Math.floor(length_second / 3600);
  const minutes = Math.floor((length_second % 3600) / 60);
  if (langJa) {
    return (
      (days > 0 ? `${days}日 ` : "") +
      (hours > 0 ? `${hours}時間 ` : "") +
      `${minutes}分`
    );
  }
  return (
    (days > 0 ? `${s(days, "day")} ` : "") +
    (hours > 0 ? `${s(hours, "hour")} ` : "") +
    s(minutes, "minute")
  );
}

// function calcAPGain(timestamp) {
//   const APGain = (timestamp * 1000 - Date.now()) / 1000 / 60 / 5;
//   const boundedAPGain = Math.max(Math.floor(APGain), 0);
//   const APText = `${boundedAPGain} AP will regen until the next raids.`;
//   return createPText(APText);
// }

// function bossesListString(bosses, prefix = "") {
//   const bossesString = prefix;
//   if (bosses.length > 1) {
//     bossesString += bosses.slice(0, -1).join(", ") + " and ";
//   }
//   bossesString += bosses.slice(-1);
//   return bossesString;
// }

interface chartConfig {
  title: string;
  syncExtremes?: (e: Highcharts.AxisSetExtremesEventObject) => void;
  xAxisTitle?: string;
  yAxisTitle?: string;
  yAxisMin?: number;
  yAxisMax?: number;
  valueDecimals?: number;
  hiddenSeries?: string[];
}

const fontFamily = `
"Fira Sans",
-apple-system, "BlinkMacSystemFont",
"Segoe UI", Helvetica, Arial,
sans-serif, "Apple Color Emoji", "Segoe UI Emoji"
`;

function genOpts(data: Map<string, [number, number][]>, config: chartConfig) {
  const series: Highcharts.SeriesOptionsType[] = [];
  for (const [boss, bossData] of data.entries()) {
    series.push({
      type: "line",
      name: boss,
      data: bossData,
      visible: !config.hiddenSeries?.includes(boss),
      // color: BOSS_COLOR[boss],
    });
  }
  return {
    chart: {
      style: {
        fontFamily: fontFamily,
      },
      zoomType: "x" as Highcharts.OptionsTypeValue,
      marginLeft: 80,
    },
    title: {
      text: config.title,
      style: {
        fontSize: "1.75em",
      },
    },
    xAxis: {
      title: {
        text: config.xAxisTitle,
        style: {
          fontSize: "1.25em",
        },
      },
      type: "datetime" as Highcharts.AxisTypeValue,
      gridLineWidth: 1,
      crosshair: {
        // width: 2,
        // color: "gray",
        dashStyle: "Dash" as Highcharts.DashStyleValue,
      },
      labels: {
        style: {
          fontSize: "1.15em",
        },
      },
      events: {
        setExtremes: config.syncExtremes,
      },
    },
    yAxis: {
      title: {
        text: config.yAxisTitle,
        style: {
          fontSize: "1.25em",
        },
      },
      labels: {
        style: {
          fontSize: "1.15em",
        },
      },
      min: config.yAxisMin,
      max: config.yAxisMax,
    },
    tooltip: {
      shadow: false,
      animation: false,
      shape: "square" as Highcharts.TooltipShapeValue,
      shared: true,
      valueDecimals: config.valueDecimals,
      style: {
        fontSize: "1.15em",
      },
      positioner: function () {
        return { x: 100, y: 50 };
      },
    },
    legend: {
      enabled: true,
      itemStyle: {
        fontSize: "1.15em",
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      sourceWidth: 1000,
      sourceHeight: 500,
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false,
        },
        lineWidth: 1,
        states: {
          hover: {
            enabled: true,
          },
          inactive: {
            enabled: false,
          },
        },
        // threshold: null,
        boostThreshold: 1,
      },
    },
    series: series,
  };
}

interface EventConfig {
  pageTitle: string;
  timezoneName: string;
  hpTitle: string;
  hpUnit: string;
  dpsTitle: string;
  dpsUnit: string;
}

interface CommonConfig {
  etaLookBack: number;
  timezoneOffset: number;
  hideFinished?: boolean;
  hpYMin?: number;
  hpYMax?: number;
  dpsYMin?: number;
  dpsYMax?: number;
}

interface BossData {
  day: number;
  name: string;
  group: number;
  hps: [number, number][];
  target: number[];
  scale: number;
  startTime: number[];
  endTime: number[];
}

interface RaidData {
  data: BossData[];
  config: CommonConfig;
  enConfig: EventConfig;
  jpConfig: EventConfig;
}

interface EtaResult {
  boss: string;
  eta: number;
  target: number;
  manyTargets: boolean;
}

interface RunTime {
  day: number;
  boss: string;
  group: number;
  target: number;
  startTime: number;
  endTime: number;
}

declare module "highcharts" {
  interface Point {
    highlight(event: PointerEvent | TouchEvent | MouseEvent): void;
  }
  interface Series {
    searchPoint(
      event: PointerEventObject,
      compareX: boolean,
    ): Point | undefined;
  }
}

async function main() {
  const raidData: RaidData = await fetch("data.json", {
    method: "GET",
    credentials: "include",
    mode: "no-cors",
  }).then((response) => response.json());
  const langJa =
    new URLSearchParams(window.location.search).get("lang") === "ja";
  const config = langJa ? raidData.jpConfig : raidData.enConfig;
  const serverJp = raidData.config.timezoneOffset === -9;
  if (serverJp) {
    const langJaLink = document.getElementById("jaLink")!;
    langJaLink.style.display = "block";
  }

  const pageTitleDiv = document.getElementById("pageTitle")!;
  pageTitleDiv.innerText = config.pageTitle;
  document.title = config.pageTitle;

  const etaResult: EtaResult[] = [];
  const runTime: RunTime[] = [];
  for (const boss of raidData.data) {
    for (let i = 0; i < boss.target.length; i++) {
      const removedZero = boss.hps.filter(([_, hp]) => hp !== 0);
      const bossTarget = boss.target[i];
      const lastBossData = nth(boss.hps, -1);
      if (
        lastBossData &&
        lastBossData[1] < bossTarget &&
        lastBossData[1] !== 0 &&
        removedZero.length > 1
      ) {
        const eta = calcETA(
          removedZero,
          bossTarget,
          raidData.config.etaLookBack,
        );
        etaResult.push({
          boss: boss.name,
          eta: eta,
          target: bossTarget,
          manyTargets: boss.target.length > 1,
        });
      } else {
        if (
          boss.startTime.length >= i + 1 &&
          boss.endTime.length >= i + 1 &&
          boss.endTime[i] !== 0
        ) {
          runTime.push({
            day: boss.day,
            boss: boss.name,
            group: boss.group,
            target: bossTarget,
            startTime:
              Math.max(...boss.hps.slice(0, 5).map((hp) => hp[1])) !== 0
                ? boss.startTime[i]
                : boss.hps.find((hp) => hp[1] !== 0)?.[0] ?? boss.startTime[i],
            endTime: boss.endTime[i],
          });
        }
      }
    }
  }

  if (etaResult.length > 0) {
    const etaElem = document.getElementById("etaText")!;
    etaResult.sort((a, b) => a.eta - b.eta);
    for (const bossEta of etaResult) {
      const [dateString, difference] = futureStrings(bossEta.eta, true, langJa);
      let etaText: string;
      if (bossEta.manyTargets) {
        etaText = `${
          bossEta.boss
        } to reach ${bossEta.target.toLocaleString()}: ${difference} (${dateString})`;
      } else {
        etaText = `${bossEta.boss}: ${difference} (${dateString})`;
      }
      etaElem.appendChild(createPText(etaText));
    }
  }

  if (runTime.length > 0) {
    const runtimeElem = document.getElementById("runtimeText")!;
    const timeZone = serverJp ? "Asia/Tokyo" : "America/Los_Angeles";
    runtimeElem.appendChild(
      createPText(langJa ? "経過時間" : "Boss run time:"),
    );
    runTime.sort((a, b) => a.endTime - b.endTime);
    for (const bossRunTime of runTime) {
      const endDate = new Date(bossRunTime.endTime * 1000);
      const zonedDate = utcToZonedTime(endDate, timeZone);
      const endTime = formatTimeStamp(
        zonedDate,
        langJa,
        serverJp ? "Asia/Tokyo" : "America/Los_Angeles",
      );
      const runTime = getDuration(
        bossRunTime.startTime,
        bossRunTime.endTime,
        langJa,
      );
      const bossInfo = `${bossRunTime.boss}: ${runTime} (${endTime})`;
      runtimeElem.appendChild(createPText(bossInfo));
    }
  }

  if (serverJp) {
    for (const elementId of ["pageTitle", "etaText", "runtimeText", "charts"]) {
      const element = document.getElementById(elementId);
      if (element !== null) {
        element.setAttribute("lang", "ja-JP");
      }
    }
  }

  Accessibility(Highcharts);
  Exporting(Highcharts);
  ExportData(Highcharts);
  Boost(Highcharts);

  /**
   * In order to synchronize tooltips and crosshairs, override the
   * built-in events with handlers defined on the parent element.
   */
  let stillGoingBoss = 0;
  let currentLength = 0;
  for (let i = 0; i < raidData.data.length; i++) {
    const boss = raidData.data[i];
    if (boss.hps.length > currentLength) {
      stillGoingBoss = i;
      currentLength = boss.hps.length;
    }
  }

  const eventTypes = ["mousemove", "mouseleave", "touchstart", "touchmove"];

  eventTypes.forEach(function (eventType) {
    document
      .getElementById("charts")!
      .addEventListener(eventType, function (e: Event) {
        for (const chart of Highcharts.charts) {
          if (chart) {
            const event = chart.pointer.normalize(
              e as MouseEvent | PointerEvent | TouchEvent,
            ); // Find coordinates within the chart
            const point = chart.series[stillGoingBoss].searchPoint(event, true);
            if (point) {
              if (["mousemove", "touchmove", "touchstart"].includes(e.type)) {
                point.onMouseOver();
                chart.xAxis[0].drawCrosshair(event, point);
              } else {
                point.onMouseOut();
                chart.tooltip.hide();
                chart.xAxis[0].hideCrosshair();
              }
            }
          }
        }
      });
  });

  /**
   * Override the reset function, we don't need to hide the tooltips and
   * crosshairs.
   */
  Highcharts.Pointer.prototype.reset = function () {
    return undefined;
  };

  /**
   * Highlight a point by showing tooltip, setting hover state and draw crosshair
   */
  Highcharts.Point.prototype.highlight = function (
    event: PointerEvent | TouchEvent | MouseEvent,
  ) {
    const highchartsEvent = this.series.chart.pointer.normalize(event);
    this.onMouseOver(); // Show the hover marker
    this.series.chart.tooltip.refresh(this); // Show the tooltip
    this.series.chart.xAxis[0].drawCrosshair(highchartsEvent, this); // Show the crosshair
  };

  /**
   * Synchronize zooming through the setExtremes event handler.
   */
  function syncExtremes(
    this: Highcharts.Series,
    e: Highcharts.AxisSetExtremesEventObject,
  ) {
    const thisChart = this.chart;
    if (e.trigger !== "syncExtremes") {
      // Prevent feedback loop
      Highcharts.charts.forEach(function (chart) {
        if (chart && chart !== thisChart) {
          if (chart.xAxis) {
            // It is null while updating
            chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
              trigger: "syncExtremes",
            });
          }
        }
      });
    }
  }

  Highcharts.setOptions({
    lang: {
      numericSymbols: ["K", "M", "B", "T", "P", "E"],
    },
    time: {
      timezoneOffset: raidData.config.timezoneOffset * 60,
    },
  });

  const hpData: Map<string, [number, number][]> = new Map();
  const dpsData: Map<string, [number, number][]> = new Map();

  for (const boss of raidData.data) {
    const lastBossData = nth(boss.hps, -1);
    if (lastBossData !== undefined && lastBossData[1] === 0) continue;
    let scaledHpData: [number, number][] = [];
    if (boss.scale > 0) {
      scaledHpData = boss.hps
        .filter(
          ([_, hp], i) =>
            hp !== 0 || (i < boss.hps.length - 1 && boss.hps[i + 1][1] !== 0),
        )
        .map((x) => [x[0], Math.max((boss.target[0] - x[1]) / boss.scale, 0)]);
    } else if (boss.scale === -1) {
      scaledHpData = boss.hps;
    }

    hpData.set(
      boss.name,
      scaledHpData.map((x) => [x[0] * 1000, x[1]]),
    );

    const rateData = rate(scaledHpData, undefined, true);
    const smoothedDpsData = ASAP(rateData, 1000) as {
      x: number;
      y: number;
    }[];
    dpsData.set(
      boss.name,
      smoothedDpsData.map((point) => [point.x * 1000, point.y]),
    );
  }

  const hiddenSeries = raidData.config.hideFinished
    ? runTime
        .filter((r) =>
          etaResult.length === 0
            ? r.group !== Math.max(...runTime.map((r) => r.group))
            : true,
        )
        .map((r) => r.boss)
    : [];

  Highcharts.chart(
    "hpChart",
    genOpts(hpData, {
      title: config.hpTitle,
      xAxisTitle: config.timezoneName,
      yAxisTitle: config.hpUnit,
      yAxisMin: raidData.config.hpYMin,
      yAxisMax: raidData.config.hpYMax,
      valueDecimals: 0,
      syncExtremes: syncExtremes,
      hiddenSeries,
    }),
  );

  Highcharts.chart(
    "dpsChart",
    genOpts(dpsData, {
      title: config.dpsTitle,
      xAxisTitle: config.timezoneName,
      yAxisTitle: config.dpsUnit,
      yAxisMin: raidData.config.dpsYMin,
      yAxisMax: raidData.config.dpsYMax,
      valueDecimals: 2,
      syncExtremes: syncExtremes,
      hiddenSeries,
    }),
  );
}

main();
